import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, LifeBuoy, MessageCircleQuestion, Send } from 'lucide-react';
import SiteBrandedLoader from '../../components/SiteBrandedLoader';
import { HAIRLINE, Panel } from '../../components/assessment/AssessmentUI';
import { PortalShell, timeAgo } from '../../components/portal/PortalShell';
import { portalService } from '../../services/api';
import { PortalQuestion, PortalTicket } from '../../types/portal';

const fa = (n: number) => n.toLocaleString('fa-IR');
const CTA_BG = 'linear-gradient(135deg,#1C3B9B,#3A2FA0)';

const inputClass =
  'w-full rounded-xl border bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition focus:border-[#9DB0E4]';

const QUESTION_STATUS: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'در انتظار پاسخ', color: '#fab219' },
  ANSWERED: { label: 'پاسخ داده شد', color: '#0ca30c' },
  ESCALATED: { label: 'ارجاع به مدرس', color: '#7C3AED' },
  CLOSED: { label: 'بسته', color: '#94A3B8' },
};

const TICKET_STATUS: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'باز', color: '#fab219' },
  IN_PROGRESS: { label: 'در حال بررسی', color: '#3B82C4' },
  RESOLVED: { label: 'حل شد', color: '#0ca30c' },
  CLOSED: { label: 'بسته', color: '#94A3B8' },
};

const PortalSupportPage: React.FC = () => {
  const location = useLocation();
  const initial = location.pathname.includes('support') ? 'tickets' : 'questions';

  const [tab, setTab] = useState<'questions' | 'tickets'>(initial as 'questions' | 'tickets');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState<PortalQuestion[]>([]);
  const [tickets, setTickets] = useState<PortalTicket[]>([]);
  const [draft, setDraft] = useState({ subject: '', body: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, t] = await Promise.all([
        portalService.getQuestions().catch(() => []),
        portalService.getTickets().catch(() => []),
      ]);
      setQuestions(q);
      setTickets(t);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'دریافت اطلاعات انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) {
      setError('عنوان و متن را کامل کنید.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (tab === 'questions') {
        await portalService.askQuestion(draft.subject.trim(), draft.body.trim());
      } else {
        await portalService.openTicket(draft.subject.trim(), draft.body.trim());
      }
      setDraft({ subject: '', body: '' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'ثبت انجام نشد.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFBFE]">
        <SiteBrandedLoader />
      </div>
    );
  }

  const items: Array<{
    id: string;
    subject: string;
    body: string;
    createdAt: string;
    status: string;
    replies: PortalQuestion['replies'];
  }> = tab === 'questions' ? questions : tickets;

  const statusMap = tab === 'questions' ? QUESTION_STATUS : TICKET_STATUS;

  return (
    <PortalShell>
      <div className="mx-auto max-w-3xl space-y-4">
        <Link to="/portal" className="inline-flex items-center gap-1.5 text-[12px] font-black text-slate-500">
          <ArrowRight className="h-4 w-4" />
          بازگشت به پرتال
        </Link>

        <div className="flex gap-1 border-b" style={{ borderColor: HAIRLINE }}>
          {(
            [
              ['questions', 'پرسش از منتور', <MessageCircleQuestion className="h-4 w-4" key="q" />],
              ['tickets', 'پشتیبانی سایت', <LifeBuoy className="h-4 w-4" key="t" />],
            ] as const
          ).map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key as 'questions' | 'tickets')}
              className={
                'flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-black transition ' +
                (tab === key
                  ? 'border-b-2 border-[#1C3B9B] text-[#1C3B9B]'
                  : 'text-slate-400 hover:text-slate-600')
              }
            >
              {icon}
              {label}
            </button>
          ))}
        </div>

        <Panel className="p-5">
          <p className="mb-3 text-[13.5px] font-black text-slate-900">
            {tab === 'questions' ? 'سؤال تازه از منتور' : 'ثبت تیکت پشتیبانی'}
          </p>
          <div className="space-y-2.5">
            <input
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              placeholder="عنوان"
              className={inputClass}
              style={{ borderColor: HAIRLINE }}
            />
            <textarea
              rows={4}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder={
                tab === 'questions'
                  ? 'سؤال خود را دقیق بنویسید تا پاسخ دقیق‌تری بگیرید.'
                  : 'مشکل را با جزئیات توضیح دهید.'
              }
              className={inputClass}
              style={{ borderColor: HAIRLINE }}
            />
            {error ? <p className="text-[11.5px] font-black text-red-600">{error}</p> : null}
            <button
              type="button"
              disabled={busy}
              onClick={send}
              className="flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-black text-white disabled:opacity-50"
              style={{ background: CTA_BG }}
            >
              <Send className="h-4 w-4" />
              {busy ? 'در حال ثبت…' : 'ارسال'}
            </button>
          </div>
        </Panel>

        <Panel className="p-4 sm:p-5">
          <p className="mb-3 text-[13.5px] font-black text-slate-900">
            {tab === 'questions' ? 'سؤال‌های شما' : 'تیکت‌های شما'} ({fa(items.length)})
          </p>

          {items.length ? (
            <div className="space-y-2.5">
              {items.map((item) => {
                const badge = statusMap[item.status] ?? { label: item.status, color: '#94A3B8' };
                return (
                  <div key={item.id} className="rounded-2xl border p-3.5" style={{ borderColor: HAIRLINE }}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 text-[13px] font-black text-slate-900">
                        {item.subject}
                      </p>
                      <span
                        className="shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-black"
                        style={{ backgroundColor: badge.color + '18', color: badge.color }}
                      >
                        {badge.label}
                      </span>
                    </div>

                    <p className="mt-1.5 whitespace-pre-line text-[12px] leading-7 text-slate-600">
                      {item.body}
                    </p>
                    <p className="mt-1 text-[10.5px] font-bold text-slate-400">
                      {timeAgo(item.createdAt)}
                    </p>

                    {item.replies.length ? (
                      <div className="mt-3 space-y-2 border-t pt-3" style={{ borderColor: HAIRLINE }}>
                        {item.replies.map((reply) => (
                          <div key={reply.id} className="rounded-xl bg-slate-50 px-3 py-2">
                            <p className="whitespace-pre-line text-[12px] leading-7 text-slate-700">
                              {reply.body}
                            </p>
                            <p className="mt-1 text-[10px] font-bold text-slate-400">
                              {[reply.author.firstName, reply.author.lastName]
                                .filter(Boolean)
                                .join(' ')}
                              {' · '}
                              {timeAgo(reply.createdAt)}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="py-8 text-center text-[12.5px] font-bold text-slate-400">
              هنوز موردی ثبت نکرده‌اید.
            </p>
          )}
        </Panel>
      </div>
    </PortalShell>
  );
};

export default PortalSupportPage;
